import { StateShortName } from '@acp/shared';
import {
    usePrefetchExperiments, usePrefetchAgencySettings,
} from '@acp/feature/auth';
import React from 'react';
import { AppFooter } from '../components/app-footer';
import { AppNavigation } from '../components/app-navigation-bar';
import { NavigationItemType } from '../components/app-navigation-bar/types';
import { usePrefetchActiveIntegrations } from '../features/integration';
import { DocumentUploadQueueProvider } from '../features/document';
import { usePrefetchUserSettings } from '../features/settings/hooks/use-user-settings';
import { useSetDatadogUserAttributes } from '../hooks/use-datadog';
import { useSetGoogleAnalyticsUserProperties } from '../hooks/use-google-analytics';
import { TermsAndConditionsModal } from '../features/settings';

const PropertyRequestMachineContext = React.lazy(() => import('../features/property-request/create-request/creator-machine/context'));
const PropertyRequestModal = React.lazy(() => import('../features/property-request/components/lazy-loaded/property-request-modal'));

export function AppLayout({ children }: { children: React.ReactNode }) {
    usePrefetchActiveIntegrations();
    usePrefetchAgencySettings();
    usePrefetchUserSettings();
    usePrefetchExperiments();
    useSetDatadogUserAttributes();
    useSetGoogleAnalyticsUserProperties();

    return (
        <PropertyRequestMachineContext>
            <DocumentUploadQueueProvider>
                <AppNavigation navItems={[
                    {
                        type: NavigationItemType.InternalLink,
                        title: 'Home',
                        to: '/',
                        collapse: false,
                        applicableStates: null,
                        applicableRoles: null,
                    },
                    {
                        type: NavigationItemType.InternalLink,
                        title: 'Portfolio',
                        to: '/portfolio',
                        collapse: false,
                        applicableStates: null,
                        applicableRoles: null,
                    },
                    {
                        type: NavigationItemType.DropdownMenu,
                        title: 'Action Required',
                        dropdownItems: [
                            {
                                type: NavigationItemType.InternalLink,
                                title: 'Required Updates',
                                to: '/action-required/update',
                                applicableStates: null,
                                applicableRoles: null,
                            },
                            {
                                type: NavigationItemType.ExternalLink,
                                title: 'Quotes',
                                to: '/Quotations/index',
                                applicableStates: null,
                                applicableRoles: null,
                            },
                            {
                                type: NavigationItemType.InternalLink,
                                title: 'Access Issues',
                                to: '/action-required/access-issues',
                                applicableStates: null,
                                applicableRoles: null,
                            },
                            {
                                type: NavigationItemType.InternalLink,
                                title: 'Reported Findings',
                                to: '/action-required/reported-findings',
                                applicableStates: null,
                                applicableRoles: null,
                            },
                        ],
                        collapse: false,
                        applicableStates: null,
                        applicableRoles: null,
                    },
                    {
                        type: NavigationItemType.ExternalLink,
                        title: 'Bookings',
                        to: '/Bookings/view',
                        collapse: true,
                        applicableStates: null,
                        applicableRoles: null,
                    },
                    {
                        type: NavigationItemType.ExternalLink,
                        title: 'Invoices',
                        to: '/Invoices/index',
                        collapse: true,
                        applicableStates: null,
                        applicableRoles: null,
                    },
                    {
                        type: NavigationItemType.DropdownMenu,
                        title: 'Keys',
                        dropdownItems: [
                            {
                                type: NavigationItemType.ExternalLink,
                                title: 'Key Management',
                                to: '/Keys/keyManagement',
                                applicableStates: null,
                                applicableRoles: null,
                            },
                            {
                                type: NavigationItemType.ExternalLink,
                                title: 'Key Collection History',
                                to: '/Keys/keyCollectionHistory',
                                applicableStates: null,
                                applicableRoles: null,
                            },
                        ],
                        collapse: true,
                        applicableStates: null,
                        applicableRoles: null,
                    },
                    {
                        type: NavigationItemType.ExternalLink,
                        title: 'Lease Checks',
                        to: '/BetweenTenancyBatches/manageTenancyChange',
                        collapse: true,
                        applicableStates: [StateShortName.QLD],
                        applicableRoles: null,
                    },
                    {
                        type: NavigationItemType.ExternalLink,
                        title: 'Service Reports & Quotes',
                        to: '/ComplianceReports/listView',
                        collapse: true,
                        applicableStates: null,
                        applicableRoles: null,
                    },
                    {
                        type: NavigationItemType.InternalLink,
                        title: 'Minimum Standards',
                        to: '/minimum-standards',
                        collapse: false,
                        applicableStates: [StateShortName.VIC],
                        applicableRoles: null,
                    },
                ]}
                />
                {children}
                <AppFooter />
                <TermsAndConditionsModal />
                <PropertyRequestModal />
            </DocumentUploadQueueProvider>
        </PropertyRequestMachineContext>
    );
}
